import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Button, Container, InputLabel, FormControl, Grid, Stack, TextField, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom';
import TypeAhead from 'components/TypeAhead';

import { useAuth } from 'hooks/useAuthContext';
import { useSnackbar } from 'components/snackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, register, watch, Controller } from 'react-hook-form';
import FormProvider from 'hooks/form/form-provider';
import { AddAdminSchema, AddCompanySchema } from 'models/AdminAndCompanySchema';
import { addAdminAndCompanyUsers } from 'api/addadminandcompany';
import { endpoints } from 'utils/axios';


import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

/**
 * AddUsersComponent is a form component for adding or updating user information.
 * 
 * Props:
 * - onSaveData: Function to handle saving or updating user data.
 * - initialData: Object containing initial form data, used for editing existing users.
 * - userOption: Array of user type options for the autocomplete field.
 * 
 * The component utilizes React Hook Form for form management and validation,
 * with schemas for admin and company user types. Depending on the selected
 * user type, the form dynamically changes to accommodate either individual
 * or company inputs. On form submission, it sends the data to an API to
 * add or update users and provides feedback via snackbars.
 */

const AddUsersComponent = ({ onSaveData, initialData, userOption }) => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [formData, setFormData] = useState(initialData || {});
    const [isCompanySelect, setIsCompanySelect] = useState(false);
    const [editCompanyUser, setEditCompanyUser] = useState(false);

    const defaultValues = {
        user_type: '',
        full_name: '',
        company: '',
        phone_number: '',
        email: '',
        user_id: '',
        recruiter_name: '',
    };

    const userType = userOption;

    const methods = useForm({
        defaultValues,
        mode: "onChange",
        resolver: yupResolver(
            isCompanySelect ? AddCompanySchema(defaultValues?.user_type) : AddAdminSchema
        )
    });

    const {
        register,
        handleSubmit,
        control,
        setValue,
        reset,
        watch,
        formState: { errors, isDirty, isValid, dirtyFields, isSubmitting }
    } = methods;

    const watchedUserType = watch('user_type');

    useEffect(() => {
        if (initialData) {
            const isCompanyOrRecruiter = initialData.user_type === 'company' || initialData.user_type === 'recruiter';
            setEditCompanyUser(initialData?.id && isCompanyOrRecruiter);
            setIsCompanySelect(isCompanyOrRecruiter);

            reset({
                user_type: initialData.user_type || '',
                full_name: initialData.full_name || '',
                company: initialData.full_name && isCompanyOrRecruiter ? initialData.full_name : '', // Set company name if it's a company user
                phone_number: initialData.phone_number || '',
                email: initialData.email || '',
                user_id: initialData.id || '',
                recruiter_name: initialData.recruiter_name || ''
            });
        } else {
            reset(defaultValues); // Reset to empty if no initialData
            setIsCompanySelect(false);
            setEditCompanyUser(false);
        }
    }, [initialData, reset]);

    // Update schema resolver when isCompanySelect changes
    useEffect(() => {
        methods.resolver = yupResolver(isCompanySelect ? AddCompanySchema(watch('user_type')) : AddAdminSchema);
    }, [isCompanySelect, methods]);


    /**
     * Handles the cancel button click event. Calls the onSaveData function, which should
     * navigate away from this component and save any unsaved data.
     */
    const onCancel = () => {
        onSaveData();
    }

    const onSubmit = handleSubmit(async (formData) => {
        if (isCompanySelect) {
            formData["full_name"] = formData?.company;
        }
        const response = await addAdminAndCompanyUsers(formData)
        if (response?.success && response.data) {
            enqueueSnackbar(response.message, { variant: 'success' })
            onSaveData(response.data);
        } else {
            enqueueSnackbar(response.message, { variant: 'error' })
        }
    });

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Box>
            <Container>
                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <Typography textAlign={'center'} variant="button" display="block" gutterBottom>
                        Add User
                    </Typography>
                    <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth >
                                <Controller
                                    control={control}
                                    name='user_type'
                                    render={({ field }) =>
                                        <Autocomplete
                                            {...field}
                                            options={userType?.map(item => item?.label)}
                                            renderInput={(params) => <TextField {...params} size="small" label="Select user type" variant="outlined"
                                                error={!!errors.user_type}
                                                helperText={errors.user_type ? errors.user_type.message : ''} />}
                                            onChange={(e, value) => {
                                                field.onChange(value);
                                                const isCompanyOrRecruiter = value === 'company' || value === 'recruiter';
                                                setIsCompanySelect(isCompanyOrRecruiter);
                                                if (!isCompanyOrRecruiter) {
                                                    setValue('company', '');
                                                    setValue('phone_number', '');
                                                    setValue('email', '');
                                                    setIsCompanySelect(false);
                                                }
                                            }}
                                            value={field.value || null}
                                        />
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth >
                                {!isCompanySelect ?
                                    <TextField
                                        name='full_name'
                                        label="Name"
                                        variant="outlined"
                                        autoComplete="full_name"
                                        placeholder='Please enter your name'
                                        fullWidth
                                        size="small"
                                        {...register('full_name')}
                                        error={!!errors.full_name}
                                        helperText={errors.full_name?.message}
                                    /> :
                                    <Controller
                                        control={control}
                                        name='company'
                                        render={({ field }) => {
                                            const handleCompanyChange = (event, selectedValue) => {
                                                if (typeof selectedValue === 'object' && selectedValue) {
                                                    const companyName = selectedValue.name;
                                                    field.onChange(companyName);
                                                    setValue('id', selectedValue.id || '', { shouldDirty: true });
                                                    setValue('phone_number', selectedValue.phone_number || '', { shouldDirty: true });
                                                    setValue('email', selectedValue.email || '', { shouldDirty: true });

                                                } else if (typeof selectedValue === 'string') {
                                                    field.onChange(selectedValue);
                                                } else {
                                                    field.onChange('');
                                                    setValue('phone_number', '', { shouldDirty: true });
                                                    setValue('email', '', { shouldDirty: true });
                                                }
                                            };

                                            return (
                                                <TypeAhead
                                                    {...field}
                                                    api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.registeredCompany}`}
                                                    fullWidth
                                                    size="small"
                                                    label="Company Name"
                                                    placeholder="Please select company"
                                                    displayTextKey="name"
                                                    onInputChange={(event, value) => {
                                                        field.onChange(value);
                                                    }}
                                                    onChange={handleCompanyChange}
                                                    value={field?.value || null}
                                                    error={!!errors?.company}
                                                    helperText={errors?.company?.message}
                                                />
                                            );
                                        }}
                                    />}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    name='phone_number'
                                    label="Phone Number"
                                    variant="outlined"
                                    size="small"
                                    placeholder='Please enter phone number'
                                    fullWidth
                                    {...register('phone_number')}
                                    InputProps={{ readOnly: editCompanyUser }}
                                    InputLabelProps={{ shrink: Boolean(watch('phone_number')) || editCompanyUser }}
                                    error={!!errors.phone_number}
                                    helperText={errors.phone_number?.message}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    size="small"
                                    name='email'
                                    label="Email Address"
                                    variant="outlined"
                                    placeholder='Please enter email address'
                                    fullWidth
                                    {...register('email')}
                                    InputProps={{ readOnly: editCompanyUser }}
                                    InputLabelProps={{ shrink: Boolean(watch('email')) || editCompanyUser }}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            </FormControl>
                        </Grid>
                        {watchedUserType === 'recruiter' && <Grid item xs={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    size="small"
                                    name='recruiter_name'
                                    label="Recruiter Name"
                                    variant="outlined"
                                    placeholder='Please enter recruiter name'
                                    fullWidth
                                    {...register('recruiter_name')}
                                    InputLabelProps={{ shrink: Boolean(watch('recruiter_name')) }}
                                    error={!!errors.recruiter_name}
                                    helperText={errors.recruiter_name?.message}
                                />
                            </FormControl>
                        </Grid>}
                    </Grid>
                    <Box textAlign={'center'} flexDirection="row" justifyContent="flex-end" sx={{ marginBottom: '1rem' }}>
                        <Button variant="contained" type="submit" sx={{ marginRight: 2 }}>{!watch('user_id') ? 'Save' : 'Update'}</Button>
                        <Button variant="contained" onClick={() => onCancel()}>Cancel</Button>
                    </Box>
                </FormProvider>
            </Container>
        </Box>
    )
}

export default AddUsersComponent;